import { useEffect, useState } from "react";
import { advisoryApi, type AdvisoryEvent } from "./advisoryApi";
import { dateTime, statusLabel } from "./format";

const STEP_NAME: Record<string, string> = {
  field: "advice information",
  "advisor-note": "an advisor note",
  "goal-input": "the goal",
  "risk-assessment": "the risk assessment",
  "portfolio-input": "the portfolio",
  "financial-situation": "the financial situation",
  "product-assessment": "knowledge & experience",
  sustainability: "sustainability",
  "wealth-plan": "the wealth plan",
  wealth_plan_add: "the wealth plan",
  wealth_plan_delete: "the wealth plan",
  product_add: "the custom portfolio",
  product_update: "the custom portfolio",
  product_delete: "the custom portfolio",
  portfolio_update: "the portfolio",
};

function describe(e: AdvisoryEvent): { title: string; detail: string | null } {
  const d = e.detail ?? {};
  const step = typeof d.step === "string" ? (STEP_NAME[d.step] ?? d.step.replace(/[-_]/g, " ")) : "a step";
  switch (e.kind) {
    case "created":
      return { title: "Advice opened", detail: typeof d.flow === "string" ? d.flow : null };
    case "step_saved":
      return { title: `Saved ${step}`, detail: typeof d.action === "string" ? d.action.replace(/_/g, " ") : null };
    case "prefill_used":
      return { title: `Used Andersen data for ${step}`, detail: null };
    case "status_changed":
      return { title: `Status: ${statusLabel(String(d.to ?? ""))}`, detail: d.from ? `from ${statusLabel(String(d.from))}` : null };
    case "submitted":
      return { title: "Advice submitted", detail: null };
    case "pdf_generated":
      return {
        title: "Advice document generated",
        detail: Array.isArray(d.templates) ? d.templates.join(", ") : null,
      };
    case "sent_for_signing":
      return { title: "Continued in Quantfolio", detail: d.processStatus ? `for ${statusLabel(String(d.processStatus)).toLowerCase()}` : null };
    case "aml_screened":
      return { title: "Screening run", detail: typeof d.provider === "string" ? d.provider : null };
    case "gate_opened":
      return {
        title: "In Andersen's pre-order gate",
        detail: `Check #${String(d.checkId ?? "?")} ${d.openedBy === "webhook" ? "opened by Quantfolio" : "opened from the workbench"}`,
      };
    case "gate_decided":
      return {
        title: d.decision === "executed" ? "Approved in the pre-order gate" : "Rejected in the pre-order gate",
        detail: typeof d.info === "string" && d.info ? d.info : null,
      };
    case "orders_ingested": {
      const orders = Number(d.orders ?? 0);
      const plans = Number(d.savingsPlans ?? 0);
      return {
        title: orders + plans > 0 ? "Orders in the order inbox" : "Checked the order inbox",
        detail:
          orders + plans > 0
            ? [orders ? `${orders} order${orders === 1 ? "" : "s"}` : null, plans ? `${plans} savings plan${plans === 1 ? "" : "s"}` : null]
                .filter(Boolean)
                .join(" · ")
            : "No orders from Quantfolio yet",
      };
    }
    case "agent_action": {
      // M15: a tool call by the advisor agent (actor "agent on behalf of <user>").
      const tool = typeof d.tool === "string" ? d.tool.replace(/^(qp|andersen)_/, "").replace(/_/g, " ") : "a tool";
      const verb = d.status === "declined" ? "Declined" : d.status === "error" ? "Failed" : d.class === "draft" ? "Saved" : "Read";
      const who = typeof d.approvedBy === "string" && d.approvedBy && !d.approvedBy.startsWith("auto") ? `approved by ${d.approvedBy}` : null;
      return {
        title: `Assistant · ${verb}: ${tool}`,
        detail: [typeof d.target === "string" ? d.target : null, who].filter(Boolean).join(" · ") || null,
      };
    }
    case "lifecycle":
      return { title: `Advice ${String(d.action ?? "changed")}`, detail: typeof d.reason === "string" ? d.reason : null };
    default:
      return { title: e.kind.replace(/_/g, " "), detail: null };
  }
}

const COLLAPSED = 8;

/** The session's own audit (per advisor — QP attributes API writes to the key owner). */
export function Timeline({ sessionId, refreshKey }: { sessionId: number; refreshKey: string }) {
  const [events, setEvents] = useState<AdvisoryEvent[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  useEffect(() => {
    advisoryApi
      .events(sessionId)
      .then((d) => setEvents(d.events))
      .catch((e) => setErr((e as Error).message));
  }, [sessionId, refreshKey]);

  if (err) return <div className="banner-error">{err}</div>;
  if (!events) return <p className="muted">Loading history…</p>;
  if (events.length === 0) return <p className="muted">No history yet.</p>;

  const newest = [...events].reverse();
  const shown = all ? newest : newest.slice(0, COLLAPSED);
  return (
    <>
      <ul className="timeline">
        {shown.map((e) => {
          const { title, detail } = describe(e);
          return (
            <li key={e.id} className="timeline-item">
              <span className="timeline-dot" />
              <div className="timeline-body">
                <div className="timeline-head">
                  <strong>{title}</strong>
                  <span className="timeline-date">
                    {e.actor} · {dateTime(e.created_at)}
                  </span>
                </div>
                {detail && <div className="muted">{detail}</div>}
              </div>
            </li>
          );
        })}
      </ul>
      {newest.length > COLLAPSED && (
        <button type="button" className="link-action" onClick={() => setAll((a) => !a)}>
          {all ? "Show fewer" : `Show all ${newest.length} events`}
        </button>
      )}
    </>
  );
}
